import React, { useMemo, useRef, useState } from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import { Typography, useTheme, Grid } from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import { Button, Checkbox, CircularProgress, FormControlLabel, FormGroup, Skeleton } from "@mui/material";
import { DeleteRounded, WarningRounded } from "@mui-symbols-material/w300";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";
import { resolveErrorMessage } from "../../../utils/resolveErrorMessage";
import { getDeleteNodeMessaging, getDeleteBlockingReasonLabels } from "../../../utils/deleteNodeMessaging";
import { DeleteBlockingReason, DeleteEffect, InstalledBaseNode } from "../../../codegen/graphql";
import InfrastructureSelect from "../../InfrastructureSelect";
import { DeleteInstalledBaseNode } from "../../../graphql/mutations/deleteInstalledBaseNode";
import { DeactivateIoTConnection } from "../../../graphql/mutations/deactivateIoTConnection";
import { DeleteInstalledBaseNodeContext } from "../../../graphql/queries/deleteInstalledBaseNodeContext";
import { DeleteInstalledBaseNodePreview } from "../../../graphql/queries/deleteInstalledBaseNodePreview";
import { resolveDeleteIotDetach } from "../../../utils/deleteIotDetach";
import NodeModalTitle from "../NodeModalTitle";

interface Props {
    open: boolean;
    onClose: () => void;
    /** The node being deleted. */
    nodeId: string;
    node: InstalledBaseNode;
    refetchFunction?: () => void;
    deleteNodeCallback?: (deletedNodeId: string) => void;
}

type FormValues = {
    deleteSubNodes: boolean;
    targetParentInstalledBaseNodeId: string | null;
};

const DeleteNodeModal = ({
    open,
    onClose,
    nodeId,
    node,
    refetchFunction,
    deleteNodeCallback
}: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const apolloClient = useApolloClient();

    const [formValues, setFormValues] = useState<FormValues>({
        deleteSubNodes: true,
        targetParentInstalledBaseNodeId: null
    });
    // PURGE is irreversible — the user must explicitly acknowledge it.
    const [purgeAcknowledged, setPurgeAcknowledged] = useState(false);

    const [deleteNode, { loading: deleteLoading }] = useMutation(DeleteInstalledBaseNode, {
        refetchQueries: ["InstalledBaseNodeList", "InstalledBaseRootNodeList"]
    });

    // No refetchQueries: the node is deleted right after, and the delete
    // mutation already refetches the tree lists.
    const [deactivateConnection, { loading: detachLoading }] = useMutation(DeactivateIoTConnection);
    const busy = detachLoading || deleteLoading;
    // Survives a failed delete after a successful detach, so a retry
    // doesn't try to detach the already-detached connection again.
    const detachDoneRef = useRef(false);

    // Sub-nodes must stay under the same client, so the target picker is scoped
    // to the deleted node's tree root (pathNodes[0], as in the Move modal).
    const contextQuery = useQuery(DeleteInstalledBaseNodeContext, {
        variables: { id: nodeId },
        skip: !open,
    });
    const contextNode = contextQuery.data?.installedBaseNode;
    const pathNodes: Array<{ id: string }> = contextNode?.pathNodes ?? [];
    const pathRootId: string | undefined = pathNodes[0]?.id;
    // Deleting a client root itself makes same-client scoping impossible — keep the
    // global picker there. A failed/empty lookup likewise falls back to unscoped so
    // the delete stays usable.
    const clientRootId: string | undefined =
        pathRootId && String(pathRootId) !== String(nodeId) ? String(pathRootId) : undefined;
    // The picker reads rootNodeId once at mount, so hold it back until scoping settles.
    const scopingLoading = contextQuery.loading;

    // Leaf = no sub-nodes → cascade checkbox and target picker are meaningless.
    // Strict === 0 keeps the loading/error case on today's checkbox+picker path.
    const isLeaf = contextNode?.childrenNodeCount === 0;

    // The preview mirrors the exact mutation arguments and re-runs on every
    // change; its effect (TRASH vs PURGE) drives all wording, its allowed flag
    // gates the confirm. Without an explicit target the backend previews the
    // re-parent-to-current-parent variant, which is what the mutation would do.
    const previewQuery = useQuery(DeleteInstalledBaseNodePreview, {
        variables: {
            id: nodeId,
            deleteSubNodes: formValues.deleteSubNodes,
            targetParentInstalledBaseNodeId:
                !formValues.deleteSubNodes && formValues.targetParentInstalledBaseNodeId
                    ? formValues.targetParentInstalledBaseNodeId
                    : undefined,
        },
        skip: !open,
        fetchPolicy: 'network-only',
    });
    const preview = previewQuery.data?.deleteInstalledBaseNodePreview;
    const previewLoading = previewQuery.loading;
    const effect: DeleteEffect | undefined = preview?.effect;
    const blockingReasons: DeleteBlockingReason[] = preview?.blockingReasons ?? [];
    const blockingReasonLabels = useMemo(
        () => getDeleteBlockingReasonLabels(preview?.installedAssetCount ?? 0, t),
        [preview?.installedAssetCount, t],
    );
    const messaging = effect
        ? getDeleteNodeMessaging({ effect, isLeaf, nodeName: node?.title }, t)
        : undefined;

    // If the context lookup failed, fall back to the node prop (both menu
    // sources select assetLocation.iotConnection). An asset's connection is
    // detached by us below; a system's is detached by the backend as part of
    // the delete, so there it only drives the warning copy.
    const iotSource = contextQuery.error ? node : contextNode;
    const detach = resolveDeleteIotDetach(iotSource);

    const resetForm = () => {
        setFormValues({
            deleteSubNodes: true,
            targetParentInstalledBaseNodeId: null
        });
        setPurgeAcknowledged(false);
        detachDoneRef.current = false;
    };

    const isSubmitDisabled = (): boolean => {
        if (busy) return true;
        // Leaf and IoT facts must be resolved before a delete may be sent.
        if (contextQuery.loading) return true;
        if (!formValues.deleteSubNodes && !formValues.targetParentInstalledBaseNodeId) {
            return true;
        }
        // The preview must have resolved and permit exactly these arguments.
        if (previewLoading || !!previewQuery.error) return true;
        if (preview?.allowed !== true) return true;
        if (messaging?.requiresAcknowledgement && !purgeAcknowledged) return true;
        return false;
    };

    const handleSubmit = async () => {
        if (detach?.kind === 'asset' && !detachDoneRef.current) {
            try {
                const res = await deactivateConnection({
                    variables: {
                        assetLocationId: detach.assetLocationId,
                        iotConnectionId: detach.iotConnectionId,
                    },
                });
                if (!res.data?.deactivateIoTConnection?.success) {
                    toast.error(res.data?.deactivateIoTConnection?.message || t('Failed to detach IoT connection.'));
                    return;
                }
                detachDoneRef.current = true;
            } catch (err) {
                console.error('Detach IoT connection error:', err);
                toast.error(resolveErrorMessage(err, t));
                return;
            }
        }

        const variables: {
            id: string;
            deleteSubNodes?: boolean;
            targetParentInstalledBaseNodeId?: string;
        } = {
            id: nodeId
        };

        if (formValues.deleteSubNodes) {
            variables.deleteSubNodes = true;
        } else {
            variables.deleteSubNodes = false;
            variables.targetParentInstalledBaseNodeId = formValues.targetParentInstalledBaseNodeId!;
        }

        deleteNode({ variables })
            .then((res) => {
                const success = res.data?.deleteInstalledBaseNode?.success;
                const message = res.data?.deleteInstalledBaseNode?.message;

                if (success) {
                    // The backend message is localized and already phase-aware
                    // ("moved to the trash" / "deleted").
                    toast.success(message || (effect === DeleteEffect.PURGE
                        ? t('Node deleted permanently.')
                        : t('Node moved to trash.')));
                    // Trashing rewrites the node's path (purging removes it) —
                    // evict so any later visit queries fresh data.
                    apolloClient.cache.evict({
                        id: apolloClient.cache.identify({ __typename: 'InstalledBaseNode', id: nodeId }),
                    });
                    apolloClient.cache.gc();
                    refetchFunction?.();
                    deleteNodeCallback?.(nodeId);
                    onClose();
                } else {
                    // The IoT connection may already be detached — sync the tree row.
                    if (detachDoneRef.current) refetchFunction?.();
                    toast.error(message || t('Error deleting node.'));
                }
            })
            .catch((err) => {
                console.error('Delete node error:', err);
                if (detachDoneRef.current) refetchFunction?.();
                toast.error(resolveErrorMessage(err, t));
            });
    };

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            fullScreen={isFullScreen}
            slotProps={{
                transition: { onExited: resetForm },
                paper: {
                    sx: {
                        minWidth: isFullScreen ? undefined : "500px",
                    },
                },
            }}
        >
            <BasicModalContent
                startIcon={<DeleteRounded />}
                onClose={busy ? undefined : onClose}
                title={
                    // Narrower modal (500px) with a startIcon → smaller cap than the 600px default.
                    <NodeModalTitle prefix={t('Delete')} node={node} maxWidth={'calc(500px - 150px)'} />
                }
            >
                <FormGroup style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Warning Message — wording follows the preview's effect */}
                    <Box
                        borderRadius="4px"
                        border="1px solid #ccc"
                        padding="12px 16px"
                        marginTop="8px"
                    >
                        {previewQuery.error ? (
                            <Typography level="body-md" color="danger">
                                {resolveErrorMessage(previewQuery.error, t)}
                            </Typography>
                        ) : !messaging || (previewLoading && !preview) ? (
                            <>
                                <Skeleton variant="text" />
                                <Skeleton variant="text" width="60%" />
                            </>
                        ) : (
                            <Typography level="body-md">
                                {messaging.warning}
                            </Typography>
                        )}
                        {detach?.kind === 'asset' && (
                            <Typography level="body-md" sx={{ mt: 1 }}>
                                {t('This asset has an active IoT connection. Deleting it will first detach the IoT connection and then delete the asset.')}
                            </Typography>
                        )}
                        {detach?.kind === 'system' && (
                            <Typography level="body-md" sx={{ mt: 1 }}>
                                {t('This system has an active IoT connection. Continuing will detach the IoT connection from the system.')}
                            </Typography>
                        )}
                    </Box>

                    {/* Delete Sub-Nodes Checkbox — hidden for leaf nodes */}
                    {contextQuery.loading ? (
                        <Skeleton variant="rounded" height={42} />
                    ) : !isLeaf && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formValues.deleteSubNodes}
                                    onChange={(e) => setFormValues({
                                        ...formValues,
                                        deleteSubNodes: e.target.checked,
                                        targetParentInstalledBaseNodeId: e.target.checked ? null : formValues.targetParentInstalledBaseNodeId
                                    })}
                                    id="deleteSubNodes"
                                    name="deleteSubNodes"
                                    disabled={busy}
                                />
                            }
                            label={t('Delete all sub-nodes')}
                        />
                    )}

                    {/* Infrastructure Select - Conditional */}
                    {!isLeaf && !formValues.deleteSubNodes && (scopingLoading ? (
                        <Skeleton variant="rounded" height={40} sx={{ mt: "8px" }} />
                    ) : (
                        <InfrastructureSelect
                            label={t('Target Parent Node')}
                            value={formValues.targetParentInstalledBaseNodeId || ""}
                            excludeNodeId={nodeId}
                            rootNodeId={clientRootId}
                            rootSelectable
                            includeTrash={false}
                            onChange={(value) => {
                                if (value === nodeId) {
                                    toast.warning(t('Cannot select the node being deleted as target parent'));
                                    return;
                                }
                                setFormValues({
                                    ...formValues,
                                    targetParentInstalledBaseNodeId: value || null,
                                })
                            }}
                            error={!formValues.targetParentInstalledBaseNodeId}
                            helperText={
                                !formValues.targetParentInstalledBaseNodeId
                                    ? t('Please select a target parent node for the sub-nodes')
                                    : t('Sub-nodes will be moved to this parent node')
                            }
                            sx={{ pt: "8px" }}
                        />
                    ))}

                    {/* Blocking reasons from the preview — the confirm stays disabled */}
                    {blockingReasons.length > 0 && (
                        <Box
                            borderRadius="4px"
                            padding="12px 16px"
                            display="flex"
                            gap="8px"
                            sx={{ backgroundColor: '#FFF4E5' }}
                        >
                            <WarningRounded sx={{ color: '#B65C11', fontSize: 20, flexShrink: 0, mt: '2px' }} />
                            <Box>
                                {blockingReasons.map((reason) => (
                                    <Typography key={reason} level="body-md" sx={{ color: '#663C00' }}>
                                        {blockingReasonLabels[reason] ?? reason}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Purge acknowledgement — only when the delete is irreversible */}
                    {messaging?.requiresAcknowledgement && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={purgeAcknowledged}
                                    onChange={(e) => setPurgeAcknowledged(e.target.checked)}
                                    id="purgeAcknowledged"
                                    name="purgeAcknowledged"
                                    disabled={busy}
                                />
                            }
                            label={t('I understand that this action cannot be undone.')}
                        />
                    )}

                    {/* Action Buttons */}
                    <Grid container spacing={1} sx={{ pt: "8px" }}>
                        <Grid xs={6}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={onClose}
                                disabled={busy}
                            >
                                {t("Cancel")}
                            </Button>
                        </Grid>
                        <Grid xs={6}>
                            <Button
                                variant="contained"
                                color={messaging?.confirmColor ?? 'error'}
                                fullWidth
                                onClick={handleSubmit}
                                disabled={isSubmitDisabled()}
                                startIcon={busy ? <CircularProgress size={16} /> : <DeleteRounded />}
                            >
                                {messaging?.confirmLabel ?? t("Delete")}
                            </Button>
                        </Grid>
                    </Grid>
                </FormGroup>
            </BasicModalContent>
        </Dialog>
    );
};

export default DeleteNodeModal;
